import type { SettingsContext } from './config/settingsContext';

export const ORG_TABS = [
  'organization',
  'integrations',
  'notifications',
  'preferences',
] as const;

export const SINGLETON_TABS = [
  'workspace',
  'integrations',
  'notifications',
  'preferences',
] as const;

export type OrgTabKey = (typeof ORG_TABS)[number];
export type SingletonTabKey = (typeof SINGLETON_TABS)[number];

// Cross-context union used only by the shared TAB_LABELS catalog.
// Per-context call sites should use TabKeyFor<C> so a key cannot escape its context.
export type SettingsTabKey = OrgTabKey | SingletonTabKey;

// TabKeyMap correlates each context with its valid keys, so e.g.
// <SettingsTabContent value="workspace" context="organization"> is a compile error.
type TabKeyMap = {
  organization: OrgTabKey;
  singleton: SingletonTabKey;
};
export type TabKeyFor<C extends SettingsContext> = TabKeyMap[C];

export function getTabsForContext<C extends SettingsContext>(
  context: C
): readonly TabKeyFor<C>[] {
  return (
    context === 'organization' ? ORG_TABS : SINGLETON_TABS
  ) as readonly TabKeyFor<C>[];
}

export function getDefaultTab<C extends SettingsContext>(
  context: C
): TabKeyFor<C> {
  return getTabsForContext(context)[0];
}

export function normalizeTabKey<C extends SettingsContext>(
  context: C,
  raw: string | null | undefined
): TabKeyFor<C> {
  if (!raw) {
    return getDefaultTab(context);
  }
  const tabs = getTabsForContext(context);
  const match = tabs.find(tab => tab === raw);
  return match ?? getDefaultTab(context);
}

const TAB_LABELS: Record<SettingsTabKey, string> = {
  organization: 'Organization',
  workspace: 'Workspace',
  integrations: 'Integrations',
  notifications: 'Notifications',
  preferences: 'Preferences',
};

export function getTabLabel(tab: SettingsTabKey): string {
  return TAB_LABELS[tab];
}
